import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import UrgencyBadge, { formatDeadline } from '../components/UrgencyBadge';
import FolderTree, { DocRow, FolderRow } from '../components/FolderTree';
import type { ProjectDetail } from './ProjectHome';

type Doc = {
  id: number;
  title: string;
  status: 'a_consulter' | 'a_traiter' | 'valide';
  urgency: 'normal' | 'urgent' | 'critique';
  deadline: string | null;
  updated_at: string;
  category_id: number | null;
  category_name: string | null;
  folder_id: number | null;
  versions_count: number;
  assigned_to: number | null;
};

const STATUS_LABEL: Record<string, string> = {
  a_consulter: 'À consulter',
  a_traiter: 'À traiter',
  valide: 'Validé',
};

export default function ProjectDocuments() {
  const { id: projectId } = useParams<{ id: string }>();
  const detail = useOutletContext<ProjectDetail>();
  const nav = useNavigate();
  const [docs, setDocs] = useState<Doc[]>([]);
  const [folders, setFolders] = useState<FolderRow[]>([]);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [assignedTo, setAssignedTo] = useState<string>('');
  const [deadline, setDeadline] = useState('');
  const [urgency, setUrgency] = useState<'normal' | 'urgent' | 'critique'>('normal');
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  async function refresh() {
    if (!projectId) return;
    const [d, f] = await Promise.all([
      api.get<{ documents: Doc[] }>(`/projects/${projectId}/documents`),
      api.get<{ folders: FolderRow[] }>(`/projects/${projectId}/folders`),
    ]);
    setDocs(d.documents);
    setFolders(f.folders);
  }
  useEffect(() => {
    refresh();
  }, [projectId]);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    if (!projectId) return;
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/documents`, {
        title,
        urgency,
        deadline: deadline || null,
        category_id: categoryId ? Number(categoryId) : null,
        assigned_to: assignedTo ? Number(assignedTo) : null,
      });
      setTitle('');
      setDeadline('');
      setUrgency('normal');
      setCreating(false);
      refresh();
    } finally {
      setBusy(false);
    }
  }

  async function addFolder(name: string, parentId: number | null) {
    if (!projectId) return;
    await api.post(`/projects/${projectId}/folders`, { name, parent_id: parentId });
    refresh();
  }
  async function renameFolder(id: number, name: string) {
    await api.patch(`/folders/${id}`, { name });
    refresh();
  }
  async function removeFolder(id: number) {
    await api.del(`/folders/${id}`);
    refresh();
  }
  async function moveDoc(docId: number, folderId: number | null) {
    await api.patch(`/documents/${docId}/move`, { folder_id: folderId });
    refresh();
  }
  async function depositInFolder(folderId: number | null, file: File) {
    if (!projectId) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', file.name);
    const url =
      folderId === null
        ? `/api/v1/projects/${projectId}/deposit`
        : `/api/v1/projects/${projectId}/folders/${folderId}/deposit`;
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Dépôt impossible' }));
      alert((err as { error?: string }).error ?? 'Dépôt impossible');
      return;
    }
    refresh();
  }

  async function exportZip() {
    if (!projectId) return;
    setExporting(true);
    try {
      const res = await fetch(`/api/v1/projects/${projectId}/export-zip`, {
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Export impossible' }));
        alert((err as { error?: string }).error ?? 'Export impossible');
        return;
      }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      const dispo = res.headers.get('Content-Disposition') ?? '';
      const m = /filename="([^"]+)"/.exec(dispo);
      a.download = m?.[1] ?? 'export.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      setExporting(false);
    }
  }

  const inWorkflow = docs.filter((d) => d.status !== 'valide');
  const validated: DocRow[] = docs
    .filter((d) => d.status === 'valide')
    .map((d) => ({
      id: d.id,
      title: d.title,
      folder_id: d.folder_id,
      status: d.status,
      urgency: d.urgency,
      versions_count: d.versions_count,
    }));

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', margin: '8px 0 16px' }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span className="card-meta">
            {inWorkflow.length} en workflow · {validated.length} validés
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn btn-ghost"
            onClick={exportZip}
            disabled={exporting || validated.length === 0}
            title="Télécharger tous les documents validés en ZIP"
          >
            {exporting ? 'Export…' : '↓ Export ZIP validés'}
          </button>
          <button className="btn btn-primary" onClick={() => setCreating((v) => !v)}>
            {creating ? 'Annuler' : '+ Nouveau document'}
          </button>
        </div>
      </div>

      {creating && (
        <form className="card" onSubmit={onCreate} style={{ marginBottom: 20 }}>
          <div className="field">
            <label>Titre</label>
            <input required value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div className="field">
              <label>Catégorie</label>
              <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                <option value="">—</option>
                {detail.categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Assigné à</label>
              <select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}>
                <option value="">— (toi-même)</option>
                {detail.members.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Urgence</label>
              <select
                value={urgency}
                onChange={(e) => setUrgency(e.target.value as typeof urgency)}
              >
                <option value="normal">Normal</option>
                <option value="urgent">Urgent</option>
                <option value="critique">Critique</option>
              </select>
            </div>
          </div>
          <div className="field">
            <label>Date butoir (facultatif)</label>
            <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
          </div>
          <button className="btn btn-primary" disabled={busy}>
            {busy ? 'Création…' : 'Créer le document'}
          </button>
        </form>
      )}

      {/* Zone du haut : documents en workflow */}
      <div className="section-title">
        <h3>📝 En cours de workflow</h3>
        <span className="card-meta">{inWorkflow.length}</span>
      </div>
      {inWorkflow.length === 0 ? (
        <div className="card card-meta" style={{ marginBottom: 20 }}>
          Aucun document en cours.
        </div>
      ) : (
        <div className="grid-2" style={{ marginBottom: 20 }}>
          {inWorkflow.map((d) => (
            <Link
              key={d.id}
              to={`/projets/${projectId}/documents/${d.id}`}
              className="card"
              style={{ display: 'block' }}
            >
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <div className="card-meta">
                  {d.category_name ?? '—'} · {STATUS_LABEL[d.status]}
                </div>
                <UrgencyBadge level={d.urgency} />
              </div>
              <div className="card-title" style={{ marginTop: 6 }}>
                {d.title}
              </div>
              <div className="card-meta" style={{ marginTop: 6 }}>
                {d.versions_count} version{d.versions_count > 1 ? 's' : ''} · échéance{' '}
                {formatDeadline(d.deadline)}
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Arbre de dossiers : documents validés */}
      <FolderTree
        folders={folders}
        docs={validated}
        onDocClick={(id) => nav(`/projets/${projectId}/documents/${id}`)}
        onAddFolder={addFolder}
        onRenameFolder={renameFolder}
        onDeleteFolder={removeFolder}
        onMoveDoc={moveDoc}
        onDepositInFolder={depositInFolder}
      />
    </>
  );
}
